"use client";

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { ENDPOINTS, IS_TEST_ENV } from "@/constants/api";
import {
    Dialog,
    DialogBackdrop,
    DialogPanel,
    TransitionChild,
} from "@headlessui/react";
import {
    Bars3Icon,
    GiftIcon,
    CreditCardIcon,
    Cog6ToothIcon,
    XMarkIcon,
    UsersIcon,
    PlayIcon,
    ChatBubbleBottomCenterTextIcon,
    WrenchIcon,
    MegaphoneIcon,
    SignalIcon,
    ArrowRightOnRectangleIcon,
    ChevronRightIcon,
    ChevronDownIcon,
    PhotoIcon,
    ChartBarIcon,
    CloudIcon,
    BellAlertIcon,
    SparklesIcon,
    FolderIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "@/context/AuthContext";
import { getVersionString, getBuildDateString } from "@/constants/version";

type IconType = React.ComponentType<React.SVGProps<SVGSVGElement>>;

type LeafChild = {
    name: string;
    href: string;
    disabled?: boolean;
    isHeader?: boolean;
};

type LeafItem = {
    kind: "item";
    name: string;
    href: string;
    icon: IconType;
    pinned?: boolean;
    badge?: string;
    children?: LeafChild[] | null;
};

type GroupItem = {
    kind: "group";
    name: string;
    icon: IconType;
    children: LeafChild[];
};

type NavEntry = LeafItem | GroupItem;

const GROUP_STATE_KEY = "sidebar.groups";

function loadGroupState(): Record<string, boolean> {
    try {
        const raw = localStorage.getItem(GROUP_STATE_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
}

function saveGroupState(state: Record<string, boolean>) {
    try {
        localStorage.setItem(GROUP_STATE_KEY, JSON.stringify(state));
    } catch {
        /* ignore quota errors */
    }
}

function getNavigationItems(
    selectedCompetition: { id: string; name: string } | null,
): NavEntry[] {
    return [
        // Pinned slot — Surfr.AI Chat lands here in Phase 5
        {
            kind: "item",
            name: "Live",
            href: "/live",
            icon: SignalIcon,
        },
        {
            kind: "item",
            name: "Users",
            href: "/users",
            icon: UsersIcon,
        },
        {
            kind: "item",
            name: "Free Subscriptions",
            href: "/free-subscriptions",
            icon: GiftIcon,
        },
        {
            kind: "item",
            name: "Stripe Subscriptions",
            href: "/stripe-subscriptions",
            icon: CreditCardIcon,
        },
        {
            kind: "item",
            name: "Competitions",
            href: "/competitions",
            icon: PlayIcon,
            children: selectedCompetition
                ? [
                      {
                          name: selectedCompetition.name,
                          href: "#",
                          disabled: true,
                          isHeader: true,
                      },
                      {
                          name: "Details",
                          href: `/competitions/details?id=${selectedCompetition.id}`,
                      },
                      {
                          name: "Users",
                          href: `/competitions/users?id=${selectedCompetition.id}`,
                      },
                      {
                          name: "Devices",
                          href: `/competitions/devices?id=${selectedCompetition.id}`,
                      },
                  ]
                : null,
        },
        {
            kind: "group",
            name: "Wind",
            icon: CloudIcon,
            children: [
                { name: "Live Stations", href: "/wind-stations" },
                { name: "Push Stations", href: "/push-stations" },
                { name: "Providers", href: "/wind-providers" },
                { name: "Alerts", href: "/wind-alerts" },
            ],
        },
        {
            kind: "group",
            name: "Marketing",
            icon: MegaphoneIcon,
            children: [
                { name: "Win-back Campaigns", href: "/campaigns" },
            ],
        },
        {
            kind: "group",
            name: "Content",
            icon: FolderIcon,
            children: [
                { name: "Whats New", href: "/whatsnew" },
                { name: "Gear", href: "/gear" },
                { name: "Advertisements", href: "/advertisements" },
                { name: "Spot Images", href: "/spot-images" },
            ],
        },
        {
            kind: "group",
            name: "System",
            icon: ChartBarIcon,
            children: [
                { name: "Status", href: "/system-status" },
                { name: "Admin Log", href: "/admin-log" },
                // Test-only: entry exists only in local/test builds (route is
                // gated the same way; backend 404s outside STAGE=TEST).
                ...(IS_TEST_ENV ? [{ name: "Test Data", href: "/test-data" }] : []),
            ],
        },
    ];
}

function getPinnedItems(): LeafItem[] {
    return [
        {
            kind: "item",
            name: "Surfr.AI Chat",
            href: "/chat",
            icon: SparklesIcon,
            pinned: true,
            badge: "BETA",
        },
    ];
}

function classNames(...classes: (string | boolean | undefined | null)[]) {
    return classes.filter(Boolean).join(" ");
}

const DEFAULT_AVATAR = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%236b7280'%3E%3Ccircle cx='12' cy='12' r='12' fill='%234b5563'/%3E%3Cpath d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z' fill='%239ca3af'/%3E%3C/svg%3E";

const Sidebar: React.FC = () => {
    const { onLogout, user } = useAuth();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    // ⌘K (mac) / Ctrl+K (other) jumps to Surfr.AI Chat from anywhere.
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                navigate("/chat");
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [navigate]);
    const [selectedCompetition, setSelectedCompetition] = useState<{
        id: string;
        name: string;
    } | null>(null);
    const [groupState, setGroupState] = useState<Record<string, boolean>>(
        () => loadGroupState(),
    );

    useEffect(() => {
        const competitionId = sessionStorage.getItem("competitionId");
        const competitionName = sessionStorage.getItem("competitionName");

        if (competitionId && competitionName) {
            setSelectedCompetition({
                id: competitionId,
                name: competitionName,
            });
        } else {
            setSelectedCompetition(null);
        }
    }, [location.pathname]);

    const navigationItems = useMemo(
        () => getNavigationItems(selectedCompetition),
        [selectedCompetition],
    );
    const pinnedItems = useMemo(() => getPinnedItems(), []);

    // Auto-open a group if the active route lives inside it.
    const activeGroupName = useMemo(() => {
        for (const entry of navigationItems) {
            if (entry.kind !== "group") continue;
            const hit = entry.children.some((c) => {
                const path = c.href.split("?")[0];
                return location.pathname === path;
            });
            if (hit) return entry.name;
        }
        return null;
    }, [navigationItems, location.pathname]);

    const isGroupOpen = useCallback(
        (name: string) => {
            if (groupState[name] !== undefined) return groupState[name];
            return name === activeGroupName;
        },
        [groupState, activeGroupName],
    );

    const toggleGroup = useCallback((name: string) => {
        setGroupState((prev) => {
            const next = { ...prev, [name]: !(prev[name] ?? false) };
            saveGroupState(next);
            return next;
        });
    }, []);

    const profileSrc = user?.profilepicid
        ? `${ENDPOINTS.IMAGES.PROFILE}/${user.profilepicid}`
        : DEFAULT_AVATAR;

    const renderLeafChild = (child: LeafChild) => {
        if (child.disabled) {
            return (
                <div
                    className={classNames(
                        "block rounded-md px-3 py-2 text-sm",
                        child.isHeader
                            ? "font-bold text-white bg-gray-700"
                            : "text-gray-500 cursor-not-allowed opacity-50",
                    )}
                >
                    {child.name}
                </div>
            );
        }
        const [childPath, childQuery] = child.href.split("?");
        return (
            <NavLink
                to={child.href}
                className={({ isActive }) => {
                    const isPathMatch = location.pathname === childPath;
                    const isQueryMatch =
                        !!childQuery &&
                        location.search.includes(childQuery.split("=")[0]);
                    return classNames(
                        isActive || (isPathMatch && (childQuery ? isQueryMatch : true))
                            ? "bg-gray-700 text-white"
                            : "text-gray-300 hover:bg-gray-700 hover:text-white",
                        "block rounded-md px-3 py-2 text-sm",
                    );
                }}
            >
                {child.name}
            </NavLink>
        );
    };

    const renderLeafItem = (item: LeafItem, expanded: boolean) => {
        const isActive =
            location.pathname === item.href ||
            (location.pathname.startsWith(item.href) && item.href !== "/");
        const pinnedClasses = item.pinned
            ? "bg-gradient-to-r from-cyan-900/40 to-purple-900/40 ring-1 ring-cyan-700/40"
            : "";
        return (
            <NavLink
                to={item.href}
                className={classNames(
                    isActive
                        ? "bg-gray-800 text-white"
                        : "text-gray-400 hover:bg-gray-800 hover:text-white",
                    pinnedClasses,
                    "group flex items-center rounded-md p-2 text-sm font-semibold transition-all duration-200",
                    expanded ? "gap-x-3" : "justify-center",
                )}
                title={!expanded ? item.name : undefined}
            >
                <item.icon aria-hidden="true" className="size-6 shrink-0" />
                {expanded && (
                    <>
                        <span className="truncate flex-1">{item.name}</span>
                        {item.badge && (
                            <span className="ml-auto text-[10px] font-bold tracking-wider rounded-full bg-cyan-600 text-white px-1.5 py-0.5">
                                {item.badge}
                            </span>
                        )}
                    </>
                )}
            </NavLink>
        );
    };

    const renderGroup = (group: GroupItem, expanded: boolean) => {
        const open = isGroupOpen(group.name);
        const showChildren = expanded && open;
        const groupHasActive = group.children.some(
            (c) => location.pathname === c.href.split("?")[0],
        );

        if (!expanded) {
            // Collapsed sidebar — render group icon only, no children.
            return (
                <button
                    type="button"
                    title={group.name}
                    onClick={() => toggleGroup(group.name)}
                    className={classNames(
                        groupHasActive
                            ? "bg-gray-800 text-white"
                            : "text-gray-400 hover:bg-gray-800 hover:text-white",
                        "group w-full flex items-center justify-center rounded-md p-2 text-sm font-semibold transition-all duration-200",
                    )}
                >
                    <group.icon aria-hidden="true" className="size-6 shrink-0" />
                </button>
            );
        }

        return (
            <>
                <button
                    type="button"
                    onClick={() => toggleGroup(group.name)}
                    className={classNames(
                        groupHasActive
                            ? "text-white"
                            : "text-gray-400 hover:bg-gray-800 hover:text-white",
                        "group w-full flex items-center gap-x-3 rounded-md p-2 text-sm font-semibold transition-all duration-200",
                    )}
                >
                    <group.icon aria-hidden="true" className="size-6 shrink-0" />
                    <span className="truncate flex-1 text-left">{group.name}</span>
                    <ChevronDownIcon
                        aria-hidden="true"
                        className={classNames(
                            "size-4 shrink-0 transition-transform duration-200",
                            open ? "rotate-0" : "-rotate-90",
                        )}
                    />
                </button>
                {showChildren && (
                    <ul className="mt-1 ml-8 space-y-1">
                        {group.children.map((child) => (
                            <li key={child.name}>{renderLeafChild(child)}</li>
                        ))}
                    </ul>
                )}
            </>
        );
    };

    const renderEntry = (entry: NavEntry, expanded: boolean) => {
        if (entry.kind === "group") return renderGroup(entry, expanded);
        return (
            <>
                {renderLeafItem(entry, expanded)}
                {expanded && entry.children && entry.children.length > 0 && (
                    <ul className="mt-1 ml-8 space-y-1">
                        {entry.children.map((child) => (
                            <li key={child.name}>{renderLeafChild(child)}</li>
                        ))}
                    </ul>
                )}
            </>
        );
    };

    const navList = (expanded: boolean) => (
        <ul className="space-y-1">
            {pinnedItems.length > 0 && (
                <>
                    {pinnedItems.map((item) => (
                        <li key={item.name}>{renderLeafItem(item, expanded)}</li>
                    ))}
                    {expanded && (
                        <li className="my-2 border-t border-gray-800" aria-hidden="true" />
                    )}
                </>
            )}
            {navigationItems.map((entry) => (
                <li key={entry.name}>{renderEntry(entry, expanded)}</li>
            ))}
        </ul>
    );

    return (
        <div>
            {/* Mobile sidebar */}
            <Dialog open={sidebarOpen} onClose={setSidebarOpen} className="relative z-50 lg:hidden">
                <DialogBackdrop
                    transition
                    className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-closed:opacity-0"
                />

                <div className="fixed inset-0 flex">
                    <DialogPanel
                        transition
                        className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-closed:-translate-x-full"
                    >
                        <TransitionChild>
                            <div className="absolute top-0 left-full flex w-16 justify-center pt-5 duration-300 ease-in-out data-closed:opacity-0">
                                <button type="button" onClick={() => setSidebarOpen(false)} className="-m-2.5 p-2.5">
                                    <span className="sr-only">Close sidebar</span>
                                    <XMarkIcon aria-hidden="true" className="size-6 text-white" />
                                </button>
                            </div>
                        </TransitionChild>
                        <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-gray-900 px-6 pb-4 ring-1 ring-white/10">
                            <div className="flex h-16 shrink-0 items-center">
                                <img alt="Surfr" src="./surfr_ondarkbg.png" className="h-8 w-auto" />
                            </div>
                            <nav className="flex flex-1 flex-col">
                                <ul className="flex flex-1 flex-col gap-y-7">
                                    <li>{navList(true)}</li>
                                    {/* Settings */}
                                    <li className="mt-auto">
                                        <Link
                                            to="#"
                                            className="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm font-semibold text-gray-400 hover:bg-gray-800 hover:text-white"
                                        >
                                            <Cog6ToothIcon aria-hidden="true" className="size-6 shrink-0" />
                                            Settings
                                        </Link>
                                    </li>
                                    {/* User Profile - Mobile */}
                                    <li className="border-t border-gray-700 pt-4">
                                        <div className="flex items-center gap-3 px-2">
                                            <img
                                                alt={user?.name || "User"}
                                                src={profileSrc}
                                                className="size-10 rounded-full bg-gray-700 object-cover"
                                                onError={(e) => {
                                                    (e.target as HTMLImageElement).src = DEFAULT_AVATAR;
                                                }}
                                            />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
                                                <p className="text-xs text-gray-400 truncate">{user?.username}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1.5 mt-2 px-2">
                                            <span className="inline-flex items-center rounded-full bg-cyan-900/50 px-2 py-0.5 text-xs font-medium text-cyan-300">
                                                Admin
                                            </span>
                                            <span className="inline-flex items-center rounded-full bg-gray-700 px-2 py-0.5 text-xs font-medium text-gray-300">
                                                ID: {user?.id}
                                            </span>
                                        </div>
                                        <button
                                            onClick={() => onLogout!()}
                                            className="mt-3 w-full flex items-center gap-3 px-2 py-2 text-sm font-semibold text-gray-400 hover:bg-gray-800 hover:text-white rounded-md transition-colors"
                                        >
                                            <ArrowRightOnRectangleIcon className="size-6 shrink-0" />
                                            Sign out
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    </DialogPanel>
                </div>
            </Dialog>

            {/* Desktop sidebar - Collapsible */}
            <div
                className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col transition-all duration-300 ease-in-out"
                style={{ width: isExpanded ? '224px' : '72px' }}
                onMouseEnter={() => setIsExpanded(true)}
                onMouseLeave={() => {
                    setIsExpanded(false);
                    setShowUserMenu(false);
                }}
            >
                <div className="flex grow flex-col gap-y-5 overflow-y-auto overflow-x-hidden bg-gray-900 px-3 pb-4">
                    {/* Logo */}
                    <div className={classNames(
                        "flex shrink-0 items-center h-16 transition-all duration-300 relative overflow-hidden",
                        isExpanded ? "justify-start px-2 mt-2" : "justify-center mt-2"
                    )}>
                        <img
                            alt="Surfr"
                            src="./logo192.png"
                            className={classNames(
                                "h-10 w-10 transition-all duration-300 absolute",
                                isExpanded
                                    ? "opacity-0 scale-75"
                                    : "opacity-100 scale-100"
                            )}
                        />
                        <img
                            alt="Surfr"
                            src="./surfr_ondarkbg.png"
                            className={classNames(
                                "h-12 w-auto transition-all duration-300",
                                isExpanded
                                    ? "opacity-100 translate-x-0"
                                    : "opacity-0 -translate-x-4"
                            )}
                        />
                    </div>

                    <nav className="flex flex-1 flex-col">
                        <ul className="flex flex-1 flex-col gap-y-7">
                            <li>{navList(isExpanded)}</li>

                            {isExpanded && (
                                <li className="mt-auto">
                                    <div className="px-2 py-2 text-xs text-gray-500 text-center border-t border-gray-800 mb-2">
                                        <span>{getVersionString()}</span>
                                        <span className="mx-1">&middot;</span>
                                        <span>{getBuildDateString()}</span>
                                    </div>
                                </li>
                            )}

                            <li className={!isExpanded ? "mt-auto" : ""}>
                                <Link
                                    to="#"
                                    className={classNames(
                                        "group flex items-center rounded-md p-2 text-sm font-semibold text-gray-400 hover:bg-gray-800 hover:text-white transition-all duration-200",
                                        isExpanded ? "gap-x-3" : "justify-center"
                                    )}
                                    title={!isExpanded ? "Settings" : undefined}
                                >
                                    <Cog6ToothIcon aria-hidden="true" className="size-6 shrink-0" />
                                    {isExpanded && <span>Settings</span>}
                                </Link>
                            </li>

                            <li className="border-t border-gray-700 pt-4 relative">
                                <button
                                    onClick={() => setShowUserMenu(!showUserMenu)}
                                    className={classNames(
                                        "w-full flex items-center rounded-md p-2 text-sm font-semibold text-gray-300 hover:bg-gray-800 transition-all duration-200",
                                        isExpanded ? "gap-x-3" : "justify-center"
                                    )}
                                >
                                    <img
                                        alt={user?.name || "User"}
                                        src={profileSrc}
                                        className="size-8 rounded-full bg-gray-700 object-cover shrink-0"
                                        onError={(e) => {
                                            (e.target as HTMLImageElement).src = DEFAULT_AVATAR;
                                        }}
                                    />
                                    {isExpanded && (
                                        <>
                                            <div className="flex-1 min-w-0 text-left">
                                                <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
                                                <p className="text-xs text-gray-400 truncate">{user?.username}</p>
                                            </div>
                                            <ChevronRightIcon className={classNames(
                                                "size-4 text-gray-400 transition-transform duration-200",
                                                showUserMenu ? "rotate-90" : ""
                                            )} />
                                        </>
                                    )}
                                </button>

                                {showUserMenu && isExpanded && (
                                    <div className="mt-2 bg-gray-800 rounded-md py-1">
                                        <div className="px-3 py-3 border-b border-gray-700">
                                            <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
                                            <p className="text-xs text-gray-400 truncate mt-0.5">{user?.username}</p>
                                            <div className="flex items-center gap-1.5 mt-2">
                                                <span className="inline-flex items-center rounded-full bg-cyan-900/50 px-2 py-0.5 text-xs font-medium text-cyan-300">
                                                    Admin
                                                </span>
                                                <span className="inline-flex items-center rounded-full bg-gray-700 px-2 py-0.5 text-xs font-medium text-gray-300">
                                                    ID: {user?.id}
                                                </span>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => onLogout!()}
                                            className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                                        >
                                            <ArrowRightOnRectangleIcon className="size-5" />
                                            Sign out
                                        </button>
                                    </div>
                                )}

                                {!isExpanded && (
                                    <div className="absolute left-full ml-2 bottom-0 hidden group-hover:block">
                                        <div className="bg-gray-800 text-white text-sm px-2 py-1 rounded whitespace-nowrap">
                                            {user?.name}
                                        </div>
                                    </div>
                                )}
                            </li>

                        </ul>
                    </nav>
                </div>
            </div>

            {/* Main content area */}
            <div className="lg:pl-[72px]">
                <div className="sticky top-0 z-40 flex h-14 shrink-0 items-center gap-x-4 bg-gray-900 px-4 lg:hidden">
                    <button
                        type="button"
                        onClick={() => setSidebarOpen(true)}
                        className="-m-2.5 p-2.5 text-gray-400"
                    >
                        <span className="sr-only">Open sidebar</span>
                        <Bars3Icon aria-hidden="true" className="size-6" />
                    </button>
                    <div className="flex-1 flex justify-center">
                        <img alt="Surfr" src="./surfr_ondarkbg.png" className="h-6 w-auto" />
                    </div>
                    <img
                        alt={user?.name || "User"}
                        src={profileSrc}
                        className="size-8 rounded-full bg-gray-700 object-cover"
                        onError={(e) => {
                            (e.target as HTMLImageElement).src = DEFAULT_AVATAR;
                        }}
                    />
                </div>

                <main className="h-[calc(100vh-3.5rem)] lg:h-screen flex flex-col bg-white lg:py-4">
                    <div className="flex-1 px-4 sm:px-6 lg:px-8 overflow-auto">
                        <Outlet />
                    </div>
                </main>
            </div>
        </div>
    );
};

export default Sidebar;
